import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Award, Plus, Edit, Trash2, Save, ArrowLeft, Loader2 } from 'lucide-react';
import toast from "react-hot-toast";
import Sidebar from '../../GlobalManagement/Pages/Sidebar';
import SearchBar from '../../GlobalManagement/Pages/SearchBar';
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterEditButton, 
  masterDeleteButton, 
  masterFormContainer, 
  masterFormSectionHeader, 
  masterFormGrid2, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterFormActionBlock, 
  masterActiveColor, 
  masterInactiveColor, 
  masterActionButtonsContainer,
  masterReadOnly,
  masterTableCellstatus,
  masterTableDatastatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const DesignationMaster = () => {
  const normalizeGrade = (value) => {
    const raw = String(value || "").trim().toUpperCase();
    if (["I", "GRADE-I", "A", "GRADE 1", "GRADE-1", "1"].includes(raw)) return "I";
    if (["II", "GRADE-II", "B", "GRADE 2", "GRADE-2", "2"].includes(raw)) return "II";
    if (["III", "GRADE-III", "C", "GRADE 3", "GRADE-3", "3"].includes(raw)) return "III";
    if (["IV", "GRADE-IV", "D", "GRADE 4", "GRADE-4", "4"].includes(raw)) return "IV";
    return "I";
  };

  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false); // Toggle between List view and Form view
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const [formData, setFormData] = useState({
    designationID: '',
    designationName: '',
    departmentName: '',
    grade: 'I',
    status: 'Active'
  });

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('/api/departments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(res.data || []);
    } catch (err) {
      toast.error("Failed to load departments");
    }
  };

  const fetchDesignations = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('/api/designations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDesignations(res.data || []);
    } catch (err) {
      toast.error("Failed to load designations");
    }
  };

  const fetchNextDesignationID = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('/api/designations/next-id', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, designationID: res.data.designationID || '' }));
    } catch (err) {
      setFormData(prev => ({ ...prev, designationID: '' }));
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchDesignations();
  }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...designations].sort((a, b) => {
      const idA = String(a.designationID || a._id || '');
      const idB = String(b.designationID || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const name = (item.designationName || '').toLowerCase();
      const code = (item.designationID || '').toLowerCase();
      const dept = (item.departmentName || '').toLowerCase();
      return name.startsWith(q) || code.startsWith(q) || dept.startsWith(q);
    });
  }, [designations, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const handleAddNew = async () => {
    setEditId(null);
    setFormData({
      designationID: '',
      designationName: '',
      departmentName: '',
      grade: 'I',
      status: 'Active'
    });
    setShowForm(true);
    await fetchNextDesignationID();
  };

  const handleEdit = (designation) => {
    setEditId(designation._id);
    setFormData({
      designationID: designation.designationID || '',
      designationName: designation.designationName || '',
      departmentName: designation.departmentName || '',
      grade: normalizeGrade(designation.grade),
      status: designation.status || 'Active'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this designation?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/designations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted successfully");
      fetchDesignations();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.designationName.trim() || !formData.departmentName) {
      toast.error("Designation name and department are required");
      return;
    }

    const duplicate = designations.find(
      (d) =>
        d.designationName.toLowerCase().trim() ===
          formData.designationName.toLowerCase().trim() &&
        d._id !== editId
    );
    if (duplicate) {
      toast.error('Designation already exists!');
      return;
    }

    setIsSaving(true);
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const payload = {
        designationID: formData.designationID,
        designationName: formData.designationName.trim().toUpperCase(),
        departmentName: formData.departmentName,
        grade: normalizeGrade(formData.grade),
        status: formData.status
      };

      if (editId) {
        await axios.put(`/api/designations/${editId}`, payload, config);
        toast.success("Updated successfully");
      } else {
        await axios.post('/api/designations', payload, config);
        toast.success("Saved successfully");
      }

      fetchDesignations();
      setShowForm(false);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to save/update designation');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
       <div className={`${masterPageShell} ${masterContentArea}`}>
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Award size={14} /></div>
              <div><h1 className={masterTitle}>Designation Master</h1></div>
            </div>
            <button 
              type="button"
              onClick={showForm ? () => setShowForm(false) : handleAddNew} 
              className={showForm ? masterPrimaryButton : masterAccentButton}
            >
              {showForm ? <><ArrowLeft size={10} /> VIEW LIST</> : <><Plus size={10} /> ADD DESIGNATION</>}
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          {!showForm ? (
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              {/* Search & Pagination Toolbar */}
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by designation, code or department..." 
                  totalItems={filteredData.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              {/* Horizontally Scrollable Table Container */}
              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} style={{ width: '60px' }}>S.No</th>
                      {/* <th className={masterTableData}>ID</th> */}
                      <th className={masterTableData} >Designation Name</th>
                      <th className={masterTableData} >Department</th>
                      <th className={masterTableData}>Grade</th>
                      <th className={masterTableDatastatus}>Status</th>
                      <th className={masterTableDatastatus}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((d, index) => (
                        <tr key={d._id} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                          {/* <td className={masterTableCell} style={{ fontFamily: 'monospace', color: '#00b4d8' }}>
                            {d.designationID}
                          </td> */}
                          <td className={masterTableCell} >
                            {d.designationName}
                          </td>
                          <td className={masterTableCell} >
                            {d.departmentName}
                          </td>
                          <td className={masterTableCell}>
                            {d.grade}
                          </td>
                          <td className={masterTableCellstatus}>
                            <span className={d.status === 'Active' ? masterActiveColor : masterInactiveColor}>
                              {d.status || 'Active'}
                            </span>
                          </td>
                          <td className={masterTableCellstatus}>
                            <div className={masterActionButtonsContainer}>
                              <button 
                                type="button" 
                                onClick={() => handleEdit(d)} 
                                className={masterEditButton}
                                title="Edit"
                              >
                                <Edit size={10} />
                              </button>
                              <button 
                                type="button" 
                                onClick={() => handleDelete(d._id)} 
                                className={masterDeleteButton}
                                title="Delete"
                              >
                                <Trash2 size={10} /> 
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No designations found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Add / Edit Form Card */
            <form onSubmit={handleSubmit} className={masterFormContainer}>
              <div className={masterFormSectionHeader}>
                <h2>{editId ? 'Edit Designation Details' : 'Create New Designation'}</h2>
              </div>

              <div className={masterFormGrid2}>
                {/* <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Designation ID</label>
                  <input
                    type="text"
                    value={formData.designationID}
                    readOnly
                    className={`${masterReadOnly} cursor-not-allowed`}
                  />
                </div> */}

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Designation Name <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={formData.designationName}
                    onChange={(e) => setFormData({...formData, designationName: e.target.value})}
                    className={masterInput}
                    placeholder="Enter designation name..."
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Department <span className="text-rose-500">*</span></label>
                  <select
                    value={formData.departmentName}
                    onChange={(e) => setFormData({...formData, departmentName: e.target.value})}
                    className={masterSelect}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                      <option key={d._id} value={d.deptName}>
                        {d.deptName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Grade</label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({...formData, grade: e.target.value})}
                    className={masterSelect}
                  >
                    <option value="I">I</option>
                    <option value="II">II</option>
                    <option value="III">III</option>
                    <option value="IV">IV</option>
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className={masterFormActionBlock}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1 rounded border border-slate-300 text-slate-700 font-semibold text-[11px] bg-white hover:bg-slate-50 transition-all uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={masterAccentButton}
                >
                  {isSaving ? (
                    <Loader2 className="animate-spin" size={10} />
                  ) : (
                    <Save size={10} />
                  )}
                  {editId ? 'UPDATE DESIGNATION' : 'SAVE DESIGNATION'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default DesignationMaster;